import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Avatar,
  Button,
  TextField,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  useTheme,
  Divider,
  Snackbar,
  Alert,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Language as WebsiteIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Code as CodeIcon,
  RocketLaunch as RocketIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebase';
import { isUserClient } from '../../utils/roles';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Premium Gradient Banner
const CoverBanner = () => (
  <Box
    sx={{
      height: { xs: 160, md: 240 },
      width: '100%',
      background: 'linear-gradient(135deg, #0F141F 0%, #1E293B 50%, #007AFF 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 80% 20%, rgba(0, 230, 118, 0.15) 0%, transparent 40%)',
      }
    }}
  />
);

const CustomTextField = ({ ...props }) => (
  <TextField
    fullWidth
    variant="outlined"
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        color: '#F5F7FA',
        bgcolor: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(5px)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
        '&.Mui-focused fieldset': { borderColor: '#3B82F6' }
      },
      '& .MuiInputLabel-root': { color: '#94A3B8' },
      '& .MuiInputLabel-root.Mui-focused': { color: '#3B82F6' },
      ...props.sx
    }}
  />
);

const Profile = () => {
  const { currentUser, updateProfile } = useAuth();
  const navigate = useNavigate();
  const clientStatus = isUserClient(currentUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    bio: '',
    location: '',
    company: '',
    position: '',
    skills: '',
    github: '',
    linkedin: '',
    website: ''
  });

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const profile = await firebaseService.getUserData(currentUser.uid);
        setProfileData({
          displayName: profile?.displayName || currentUser.displayName || '',
          bio: profile?.bio || '',
          location: profile?.location || '',
          company: profile?.company || '',
          position: profile?.position || '',
          skills: profile?.skills || '',
          github: profile?.github || '',
          linkedin: profile?.linkedin || '',
          website: profile?.website || ''
        });
      } catch (error) {
        console.error('Error loading profile:', error);
        setSnackbar({ open: true, message: 'Failed to load profile.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      if (!profileData.displayName?.trim()) throw new Error('Display name is required');
      await firebaseService.updateUserProfile(currentUser.uid, profileData);
      setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update profile.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const storage = getStorage();
      const storageRef = ref(storage, `profile_photos/${currentUser.uid}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(snapshot.ref);
      await updateProfile({ photoURL });
      setSnackbar({ open: true, message: 'Photo updated!', severity: 'success' });
    } catch (error) {
      console.error('Error uploading photo:', error);
      setSnackbar({ open: true, message: 'Failed to update photo.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#0A0E17">
        <CircularProgress sx={{ color: '#3B82F6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0A0E17', pb: 8 }}>
      <CoverBanner />

      <Container maxWidth="lg" sx={{ mt: -10, position: 'relative', zIndex: 10 }}>
        <Grid container spacing={4}>
          {/* Left Column: Avatar & Quick Info */}
          <Grid item xs={12} md={4}>
            <Fade in={true} timeout={500}>
              <Card sx={{
                bgcolor: 'rgba(15, 20, 31, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                overflow: 'visible'
              }}>
                <CardContent sx={{ textAlign: 'center', pt: 0, pb: 4, px: 3 }}>
                  <Box position="relative" display="inline-block" sx={{ mt: -8, mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                    />
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={currentUser.photoURL || '/default-avatar.png'}
                        alt={profileData.displayName}
                        sx={{
                          width: 160,
                          height: 160,
                          border: '6px solid #0A0E17',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
                          bgcolor: '#1E293B'
                        }}
                      />
                      <label htmlFor="photo-upload">
                        <IconButton
                          component="span"
                          disabled={loading}
                          sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 10,
                            bgcolor: '#3B82F6',
                            color: 'white',
                            '&:hover': { bgcolor: '#2563EB' },
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                            width: 40,
                            height: 40
                          }}
                        >
                          <PhotoCameraIcon fontSize="small" />
                        </IconButton>
                      </label>
                    </Box>
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#F5F7FA', mb: 0.5, fontSize: '1.75rem' }}>
                    {profileData.displayName || currentUser?.email || currentUser?.phoneNumber || 'Founder'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 1, fontWeight: 500 }}>
                    {currentUser?.email || currentUser?.phoneNumber || ''}
                  </Typography>

                  {currentUser?.email ? (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {currentUser?.emailVerified ? (
                        <Chip
                          size="small"
                          label="Email Verified"
                          icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#10b981 !important' }} />}
                          sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.25)', fontWeight: 600, fontSize: '11px' }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Verify Email"
                          clickable
                          onClick={() => navigate('/verify-email')}
                          sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)', fontWeight: 600, fontSize: '11px' }}
                        />
                      )}
                    </Box>
                  ) : currentUser?.phoneNumber ? (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Chip
                        size="small"
                        label="Phone Verified"
                        icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#06b6d4 !important' }} />}
                        sx={{ bgcolor: 'rgba(6, 182, 212, 0.1)', color: '#22d3ee', border: '1px solid rgba(6, 182, 212, 0.25)', fontWeight: 600, fontSize: '11px' }}
                      />
                    </Box>
                  ) : null}
                  <Typography variant="body1" sx={{ color: '#94A3B8', mb: 3, fontWeight: 500 }}>
                    {profileData.position || 'Member'} {profileData.company && <span style={{ opacity: 0.6 }}>at {profileData.company}</span>}
                  </Typography>

                  {!isEditing && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        color: '#60A5FA',
                        '&:hover': {
                          borderColor: '#60A5FA',
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                          boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)'
                        }
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}

                  <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                  <Box sx={{ textAlign: 'left', px: 1 }}>
                    <Box display="flex" alignItems="center" mb={2.5}>
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', mr: 2 }}>
                        <LocationIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" color="#64748B" fontWeight={600} mb={0.5}>LOCATION</Typography>
                        <Typography variant="body2" color="#F5F7FA" fontWeight={500}>
                          {profileData.location || "Not set"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', mr: 2 }}>
                        <BusinessIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" color="#64748B" fontWeight={600} mb={0.5}>COMPANY</Typography>
                        <Typography variant="body2" color="#F5F7FA" fontWeight={500}>
                          {profileData.company || "Not set"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                    {profileData.github && (
                      <IconButton href={profileData.github} target="_blank" rel="noopener noreferrer" sx={{ bgcolor: 'rgba(255,255,255,0.03)', color: '#94A3B8', '&:hover': { color: '#F5F7FA', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <GitHubIcon />
                      </IconButton>
                    )}
                    {profileData.linkedin && (
                      <IconButton href={profileData.linkedin} target="_blank" rel="noopener noreferrer" sx={{ bgcolor: 'rgba(255,255,255,0.03)', color: '#94A3B8', '&:hover': { color: '#0077b5', bgcolor: 'rgba(0, 119, 181, 0.1)' } }}>
                        <LinkedInIcon />
                      </IconButton>
                    )}
                    {profileData.website && (
                      <IconButton href={profileData.website} target="_blank" rel="noopener noreferrer" sx={{ bgcolor: 'rgba(255,255,255,0.03)', color: '#94A3B8', '&:hover': { color: '#00E676', bgcolor: 'rgba(0, 230, 118, 0.1)' } }}>
                        <WebsiteIcon />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* Right Column: Details & Edit Form */}
          <Grid item xs={12} md={8}>
            <Fade in={true} timeout={700}>
              <Card sx={{
                bgcolor: 'rgba(15, 20, 31, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  {/* Startup Launchpad Tier Status Card */}
                  <Box
                    sx={{
                      p: 3,
                      mb: 4,
                      borderRadius: 3,
                      bgcolor: clientStatus ? 'rgba(6, 182, 212, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid',
                      borderColor: clientStatus ? 'rgba(6, 182, 212, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <RocketIcon sx={{ color: clientStatus ? '#06b6d4' : '#94A3B8', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: clientStatus ? '#06b6d4' : '#E2E8F0', letterSpacing: '0.5px' }}>
                          {clientStatus ? 'LAUNCHPAD STATUS: VERIFIED CLIENT' : 'LAUNCHPAD STATUS: FOUNDER ACCOUNT (ACTIVE)'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#94A3B8', maxWidth: 480 }}>
                        {clientStatus
                          ? 'Your account has been granted Client privileges by Verve AI Labs. Access your dedicated Project Hub, milestone tracking, and change request portal below.'
                          : 'Your account is active on VerveAI Launchpad. Once our engineering administration team assigns your workspace, your live Project Hub and deliverables tracker will unlock.'}
                      </Typography>
                    </Box>
                    {clientStatus ? (
                      <Button
                        variant="contained"
                        onClick={() => navigate('/project')}
                        sx={{
                          background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '13px',
                          textTransform: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Open Project Hub &rarr;
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        component="a"
                        href="mailto:admin@verveai.co?subject=Project%20Workspace%20Activation%20Request"
                        sx={{
                          borderColor: '#334155',
                          color: '#06b6d4',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Contact Team
                      </Button>
                    )}
                  </Box>

                  <Box display="flex" alignItems="center" mb={4}>
                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, #007AFF 0%, #00C6FF 100%)', mr: 2, boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)' }}>
                      <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" fontWeight="700" color="#F5F7FA">
                      {isEditing ? "Edit Profile Details" : "About Me"}
                    </Typography>
                  </Box>

                  {isEditing ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <CustomTextField
                          label="Display Name"
                          name="displayName"
                          value={profileData.displayName}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          label="Bio"
                          name="bio"
                          multiline
                          rows={4}
                          value={profileData.bio}
                          onChange={handleChange}
                          placeholder="Tell the community about yourself..."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="Company"
                          name="company"
                          value={profileData.company}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CustomTextField
                          label="Position"
                          name="position"
                          value={profileData.position}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          label="Location"
                          name="location"
                          value={profileData.location}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          label="Skills (comma separated, e.g. React, Python, AI)"
                          name="skills"
                          value={profileData.skills}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: '#94A3B8', mb: 2, mt: 1, fontWeight: 600 }}>SOCIAL LINKS</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CustomTextField
                          label="GitHub URL"
                          name="github"
                          value={profileData.github}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CustomTextField
                          label="LinkedIn URL"
                          name="linkedin"
                          value={profileData.linkedin}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CustomTextField
                          label="Website URL"
                          name="website"
                          value={profileData.website}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => setIsEditing(false)}
                            disabled={saving}
                            sx={{
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                              color: '#94A3B8',
                              borderRadius: 2,
                              px: 3,
                              '&:hover': { borderColor: '#F5F7FA', color: '#F5F7FA', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                            sx={{
                              background: 'linear-gradient(135deg, #007AFF 0%, #00E676 100%)',
                              fontWeight: 600,
                              px: 4,
                              borderRadius: 2,
                              boxShadow: '0 4px 15px rgba(0, 230, 118, 0.3)',
                              '&:hover': {
                                boxShadow: '0 6px 20px rgba(0, 230, 118, 0.4)',
                              }
                            }}
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box>
                      <Typography variant="body1" sx={{ color: '#B0B3C0', lineHeight: 1.8, mb: 5, fontSize: '1.05rem' }}>
                        {profileData.bio || (
                          <span style={{ fontStyle: 'italic', opacity: 0.6 }}>
                            No bio added yet. Tell us about your journey!
                          </span>
                        )}
                      </Typography>

                      <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                      <Box>
                        <Box display="flex" alignItems="center" mb={3}>
                          <CodeIcon sx={{ color: '#00E676', mr: 1.5 }} />
                          <Typography variant="h6" sx={{ color: '#F5F7FA', fontWeight: 600 }}>
                            Skills & Expertise
                          </Typography>
                        </Box>

                        {profileData.skills ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {profileData.skills.split(',').map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill.trim()}
                                sx={{
                                  bgcolor: 'rgba(0, 122, 255, 0.1)',
                                  color: '#60A5FA',
                                  border: '1px solid rgba(0, 122, 255, 0.2)',
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  py: 0.5,
                                  '&:hover': { bgcolor: 'rgba(0, 122, 255, 0.2)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0, 122, 255, 0.2)' },
                                  transition: 'all 0.2s'
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No skills listed yet.</Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Profile;
